<template lang="pug">
    .log-water
      .log-water-content
        .log-water-header How many glasses of water did you drink?
        .enter-water-input
          .counter-container
            WtSvgIcon(@click.native="decrement" icon="minus" height="37px" width="38px" style="width:38px;height:37px; cursor:pointer" :class="{ 'disabled-icon': water <= 0 }")
            .centered-input(:class="{ 'error': showError, 'valid': isValid }")
              input(
                  type="text"
                  name="water"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  v-model="water"
                  placeholder="0"
                  min="1"
                  max="25"
                  @input="filterDigits"
              )
            WtSvgIcon(@click.native="increment" icon="plus" height="37px" width="38px" style="width:38px;height:37px; cursor:pointer" :class="{ 'disabled-icon': water >= 25 }")
          .error-message(v-if="showError") The value you have entered is not valid. Check the value and try again.
        .save-button
          WtButton(
            @click.native="saveEntry"
            :loading="savingwater"
            :disabled="!isValid"
          ) Next
  </template>

<script>
import { mapActions } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";

export default {
  name: "Logwater",

  components: {
    WtTitle,
    WtButton,
    WtSvgIcon
  },

  mixins: [analyticsTrackingMixin, dateUtilityMixin],

  props: {
    uniqueKey: {
      type: String,
      default: null
    },

    entryItem: {
      type: Object,
      default: () => ({}),
      required: true
    },

    entryDate: {
      type: String,
      default: ""
    },

    formData: {
      type: FormData,
      required: true
    },

    saveEntryWrapper: {
      type: Function,
      required: true
    },

    disableSave: {
      type: Boolean,
      default: true
    },

    disableReset: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      water: 1,
      savingwater: false
    };
  },

  computed: {
    currentMetricValue() {
      let metric = this._.find(this.entryItem.wellnessMetrics, {
        type: "water"
      });
      return this._.get(metric, "value", "");
    },

    setFormData() {
      if (this._.isEmpty(this.entryItem)) return true;
      return this.water != this.currentMetricValue;
    },
    isValid() {
      return this.water && this.water >= 1 && this.water <= 25;
    },

    showError() {
      return this.water && !this.isValid;
    }
  },

  watch: {
    water() {
      if (this.setFormData) {
        this.formData.set("water", this.water);
        this.$emit("formDataUpdated", true);
      }
    },

    entryItem: {
      handler() {
        this.water = this.currentMetricValue;
      },
      immediate: true,
      deep: true
    },

    // Watch for uniqueKey changes to reset the form when switching dates
    uniqueKey: {
      handler() {
        this.water = this.currentMetricValue;
      },
      immediate: true
    }
  },

  mounted() {
    this.trackFeatureView("tracking", "tracking_water");
    this.water = this.currentMetricValue;
  },

  methods: {
    ...mapActions("members", ["storeJournal", "updateJournal"]),
    filterDigits(e) {
      e.target.value = e.target.value.replace(/\D+/g, "");
      this.water = e.target.value;
    },
    increment() {
      if (this.water < 25) this.water++;
    },
    decrement() {
      if (this.water > 0) this.water--;
    },

    saveEntry() {
      // Always send the value so the backend links the post to this metric
      this.formData.set("water", this.water);

      this.savingwater = true;
      this.saveEntryWrapper(this.storeJournal, this.updateJournal).finally(
        () => {
          this.savingwater = false;
        }
      );
    },

    reset() {
      const item = "water";
      if (this.formData.has(item)) {
        this.formData.delete(item);
      }
      this.steps = this.currentMetricValue;
      this.$nextTick(() => {
        this.$emit("formReset", true);
      });
    }
  }
};
</script>

<style lang="sass" scoped>
.log-water
  height: 100%
  display: flex
  flex-direction: column
  justify-content: space-between

.log-water-content
  flex: 1
  flex-direction: column
  display: flex
  justify-content: center
  align-items: center
  gap: 20px

  .log-water-header
    width: 100%
    text-align: left
    font-weight: 600
    font-size: 20px
    line-height: 28px
    letter-spacing: 0px
    color: #1B1D20

  .enter-water-input
    width: 100%

  .counter-container
      display: flex
      align-items: center
      justify-content: space-between
      gap: 10px
      width: 100%

      ::v-deep .control
        width: 100%

  .icon-button
    border-radius: 49px
    width: 38px
    height: 37px
    display: flex
    align-items: center
    justify-content: center

  .save-button
    width: 100%

    ::v-deep .button
      width: 100% !important
      justify-content: center
      font-size: 16px
      line-height: 24px
      letter-spacing: 0px
      background-color: #2972F0
      font-weight: 600

      &:disabled
        background-color: #D1D5DB !important
        border-color: #D1D5DB !important
        color: #9CA3AF !important
        cursor: not-allowed


.centered-input
  border: 1px solid #e5e7eb
  border-radius: 12px
  background-color: white
  transition: all 0.2s ease
  display: flex
  align-items: center
  justify-content: center

  &.valid
    border-color: #3b82f6
    background-color: #eff6ff

  &.error
    border-color: #ef4444
    background-color: #fef2f2

  ::v-deep input
    font-size: 28px
    line-height: 36px
    letter-spacing: -1%
    font-weight: 500
    width: 100%
    padding: 0px
    height: 54px
    color: #6b7280
    border: none
    background: transparent
    text-align: center
    padding: 0
    margin: 0
    -webkit-appearance: none
    -moz-appearance: textfield

    &:focus
      outline: none

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button
      -webkit-appearance: none
      margin: 0
      display: none

.log-water-form-controls
  margin-top: 20px

.disabled-icon
  opacity: 0.4
  cursor: not-allowed
  pointer-events: none

.log-tracking-btn
  display: flex
  align-items: center
  justify-content: center
  width: 32px
  height: 32px,

.error-message
  width: 100%
  text-align: left
  font-size: 13px
  color: #EC493F
  margin-top: 10px
  line-height: 18px
</style>
